import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import AddressAutocompleteInput from '../AddressAutocompleteInput';
import classNames from '../../../utils/classNames';
import './DiscoverMapSearchControl.scss';

const SearchIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
    </svg>
);

export default function DiscoverMapSearchControl({
    t,
    value = '',
    onChange,
    onSelect,
    disabled = false,
}) {
    const [isExpanded, setIsExpanded] = useState(false);
    const rootRef = useRef(null);
    const inputId = 'discover-map-address-search';
    const toggleLabel = isExpanded
        ? t('discoverPage.search.collapse', 'Collapse location search')
        : t('discoverPage.search.expand', 'Search map location');

    useEffect(() => {
        if (!isExpanded) {
            return;
        }

        const input = rootRef.current?.querySelector('input');
        input?.focus?.();
    }, [isExpanded]);

    const handleToggle = () => {
        if (disabled) {
            return;
        }
        setIsExpanded((current) => !current);
    };

    return (
        <aside
            ref={rootRef}
            className={classNames('discover-map-search-control', {
                'discover-map-search-control--expanded': isExpanded,
            })}
            role="search"
            aria-label={t('discoverPage.search.aria', 'Map location search')}
        >
            <button
                type="button"
                className="discover-map-search-control__toggle"
                onClick={handleToggle}
                aria-label={toggleLabel}
                aria-expanded={isExpanded}
                title={toggleLabel}
                disabled={disabled}
            >
                <SearchIcon />
            </button>
            <div className="discover-map-search-control__field-shell" aria-hidden={!isExpanded}>
                <AddressAutocompleteInput
                    id={inputId}
                    value={value}
                    onChange={onChange}
                    onSelect={onSelect}
                    disabled={disabled || !isExpanded}
                    placeholder={t('discoverPage.search.placeholder', 'Search address')}
                    loadingText={t('discoverPage.search.loading', 'Searching addresses...')}
                    emptyText={t('discoverPage.search.empty', 'No matching addresses')}
                    className="discover-map-search-control__field"
                />
            </div>
        </aside>
    );
}

DiscoverMapSearchControl.propTypes = {
    t: PropTypes.func.isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};
